/**
 * Der Tag eines Mitarbeiters, und wie er sich ohne Netz weiterentwickelt.
 *
 * Ohne Netz kann die Maske nicht neu laden, sie bekaeme die Kopie von vorhin
 * zurueck. Wer dann auf "Arbeitstag beginnen" tippt, saehe den Knopf danach
 * unveraendert dastehen und tippte ein zweites Mal. Deshalb rechnet die
 * Anwendung die Wirkung einer Stempelung selbst nach.
 *
 * Bewusst als reine Funktion in einer eigenen Datei: sie hat keine
 * Nebenwirkungen, sie bekommt einen Zustand und gibt den naechsten zurueck,
 * und genau so laesst sie sich pruefen. Diese Rechnung entscheidet darueber,
 * was ein Mitarbeiter im Funkloch auf dem Bildschirm sieht, und eine falsche
 * Anzeige an dieser Stelle fuehrt zu doppelten Stempelungen.
 *
 * Sie ersetzt nicht den Server. Sobald wieder Netz da ist, wird geladen, und
 * dann gilt, was der Server sagt.
 */

export interface Termin {
  id: string;
  /** "termin" ist Arbeit beim Kunden, "pause" die geplante Ruhe dazwischen. */
  art: "termin" | "pause";
  datum: string;
  von: string;
  bis: string;
  /**
   * Wer sonst noch mitfaehrt.
   *
   * Vier Stunden Garten koennen zwei Gaertner zwei Stunden sein. Wer das am
   * Morgen nicht sieht, faehrt allein hin und wartet, oder macht die Arbeit
   * doppelt.
   */
  mitglieder?: { id: string; name: string }[];
  abteilung?: string | null;
  objektId: string | null;
  objekt: string | null;
  kunde: string | null;
  /** "Herr Meier", "Familie Stoll". Die Ueberschrift des Termins. */
  ansprache: string | null;
  anschrift: string | null;
  ziel: string | null;
  hatKoordinate: boolean;
  checklisteId: string | null;
  auftragId: string | null;
  notiz: string | null;
  /**
   * Die Aenderung fuer diesen Tag, rot ganz oben am Termin.
   *
   * Optional, weil eine Kopie auf dem Geraet von gestern das Feld noch nicht
   * kennt. Dann steht keine Aenderung da, statt dass die Ansicht abstuerzt:
   * der Plan ist wichtiger als der Zusatz.
   */
  aenderung?: string | null;
  aenderungGelesenAm?: string | null;
  konflikt?: { art: string; hinweis: string } | null;
}

export interface Buchung {
  id: string;
  art: "day" | "assignment";
  terminId: string | null;
  geplant: boolean | null;
  beginn: string;
  ende: string | null;
  objektId: string | null;
  objekt: string | null;
  pauseOffen: boolean;
  pausenSekunden: number;
  sekunden: number;
}

export interface Tag {
  tag: string;
  termine: Termin[];
  buchungen: Buchung[];
  tagOffen: Buchung | null;
  einsatzOffen: Buchung | null;
  vergessen: boolean;
  heuteSekunden: number;
  /**
   * Nach wie vielen gearbeiteten Minuten die Pause faellig ist.
   *
   * Kommt vom Server aus denselben Einstellungen, mit denen der Plan rechnet.
   * Stuende die Zahl hier noch einmal, erinnerte das Telefon irgendwann zu
   * einer Zeit, die im Plan gar nicht vorkommt.
   */
  pauseNachMinuten?: number;
}

/** Eine Buchung, die es nur auf dem Geraet gibt. Sie traegt eine erkennbare Kennung. */
export const NUR_LOKAL = "lokal-";

/**
 * Eine Buchung abschliessen und ihre Dauer ausrechnen.
 *
 * Ohne diese Rechnung stuende in der Tagesliste nach einem Einsatz ohne Netz
 * eine Dauer von null. Das sieht aus wie ein verlorener Eintrag, ist aber nur
 * eine nicht gerechnete Zahl, und der Unterschied ist am Objekt nicht zu
 * erkennen. Die Pausen bleiben aussen vor: sie haengen am Arbeitstag, und die
 * genaue Zahl liefert ohnehin der Server beim naechsten Laden.
 */
function beenden(b: Buchung, ende: string): Buchung {
  const dauer = Math.max(
    0,
    Math.round((new Date(ende).getTime() - new Date(b.beginn).getTime()) / 1000)
  );
  return { ...b, ende, sekunden: Math.max(0, dauer - b.pausenSekunden) };
}

function neueBuchung(art: "day" | "assignment", zeit: string, teil: Partial<Buchung> = {}): Buchung {
  return {
    /*
     * Die Art gehoert in die Kennung. Beginnt ein Einsatz, ohne dass der
     * Arbeitstag laeuft, entstehen beide im selben Augenblick; ohne die Art
     * traegen sie dieselbe Kennung, und das spaetere Filtern erwischt die
     * falsche Buchung.
     */
    id: `${NUR_LOKAL}${art}-${zeit}`,
    art,
    terminId: null,
    geplant: null,
    beginn: zeit,
    ende: null,
    objektId: null,
    objekt: null,
    pauseOffen: false,
    pausenSekunden: 0,
    sekunden: 0,
    ...teil
  };
}

/**
 * Was eine Stempelung am angezeigten Tag aendert.
 *
 * Der Pfad entscheidet, wie beim Server auch. Ein unbekannter Pfad laesst den
 * Zustand unveraendert, statt zu raten.
 */
export function nachStempelung(
  daten: Tag,
  pfad: string,
  koerper: Record<string, unknown>,
  zeit: string
): Tag {
  const termin = daten.termine.find((x) => x.id === koerper.terminId) ?? null;

  switch (pfad) {
    case "/zeiten/tag/start":
      if (daten.tagOffen) return daten;
      return { ...daten, tagOffen: neueBuchung("day", zeit) };

    case "/zeiten/tag/ende": {
      if (!daten.tagOffen) return daten;
      /*
       * Der laufende Einsatz endet mit dem Arbeitstag. Genau so macht es der
       * Server, und wenn die Anzeige es anders macht, steht dort ein Einsatz,
       * den es nicht mehr gibt.
       */
      const beendet: Buchung[] = [
        beenden(daten.tagOffen, zeit),
        ...(daten.einsatzOffen ? [beenden(daten.einsatzOffen, zeit)] : [])
      ];
      return {
        ...daten,
        tagOffen: null,
        einsatzOffen: null,
        vergessen: false,
        buchungen: [...daten.buchungen.filter((b) => !beendet.some((x) => x.id === b.id)), ...beendet]
      };
    }

    case "/zeiten/pause":
      if (!daten.tagOffen) return daten;
      return {
        ...daten,
        tagOffen: { ...daten.tagOffen, pauseOffen: true },
        // Die Pause beendet den Einsatz, auch das wie beim Server.
        einsatzOffen: null
      };

    case "/zeiten/pause/ende":
      if (!daten.tagOffen) return daten;
      return { ...daten, tagOffen: { ...daten.tagOffen, pauseOffen: false } };

    case "/zeiten/einsatz/start": {
      const tagOffen = daten.tagOffen ?? neueBuchung("day", zeit);
      const neu = neueBuchung("assignment", zeit, {
        terminId: termin?.id ?? null,
        geplant: Boolean(termin),
        objektId: termin?.objektId ?? (koerper.objektId as string | undefined) ?? null,
        objekt: termin?.objekt ?? null
      });
      return {
        ...daten,
        tagOffen,
        einsatzOffen: neu,
        buchungen: daten.einsatzOffen
          ? [...daten.buchungen.filter((b) => b.id !== daten.einsatzOffen!.id),
             beenden(daten.einsatzOffen, zeit)]
          : daten.buchungen
      };
    }

    case "/zeiten/einsatz/ende": {
      if (!daten.einsatzOffen) return daten;
      const beendet = beenden(daten.einsatzOffen, zeit);
      return {
        ...daten,
        einsatzOffen: null,
        buchungen: [...daten.buchungen.filter((b) => b.id !== beendet.id), beendet]
      };
    }

    default:
      return daten;
  }
}
